import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import {
  Badge
} from "react-bootstrap";
import {
  ArrowLeft,
  AlertTriangle,
  Clock,
  Send,
} from "react-feather";

import {
  FaCalendarAlt,
} from "react-icons/fa";

import SMSBulkPopup from "./SMSBulkPopup";
import Sidebar from "./sidebar";
import RetentionOpportunityCard from "./RetentionOpportunityCard";
import SkeletonUILoader from "./SkeletonUILoader";

const RetentionRunDetailPage = () => {
  const { runUuid } = useParams();

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [loading, setLoading] = useState(true);

  const [run, setRun] = useState(null);

  const [opportunities, setOpportunities] = useState([]);

  const [selectedTab, setSelectedTab] = useState("all");

  const [showPopup, setShowPopup] = useState(false);

  const [popupTasks, setPopupTasks] = useState([]);

  const [summary, setSummary] = useState({
    total: 0,
    refill: 0,
    reward: 0,
    reorder: 0,
    estimatedRevenue: 0,
  });

    const [sortBy, setSortBy] = useState("expected_value");

    const [selectedMessages, setSelectedMessages] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");

    const canLaunchCampaign = run?.status === "locked";

  // ======================================================
  // FETCH RUN
  // ======================================================

  const fetchRun = async () => {
    try {
      setLoading(true);

      const response = await api.get(
        `${baseUrl}/retention/runs/${runUuid}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "access_token"
            )}`,
          },
        }
      );

      const data = response.data;

      setRun(data.run || {});

      const opps = data.opportunities || [];

      setOpportunities(opps);

      const refill = opps.filter(
        (o) => o.message_type === "refill"
      );

      const reward = opps.filter(
        (o) => o.message_type === "reward"
      );

      const reorder = opps.filter(
        (o) => o.message_type === "reorder"
      );

      const estimatedRevenue = opps.reduce(
        (acc, item) =>
          acc + parseFloat(item.expected_value || 0),
        0
      );

      setSummary({
        total: opps.length,
        refill: refill.length,
        reward: reward.length,
        reorder: reorder.length,
        estimatedRevenue,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRun();
  }, [runUuid]);

  useEffect(() => {
    setSelectedMessages(opportunities.map((o) => o.id));
  }, [opportunities]);

  useEffect(() => {
    if (canLaunchCampaign) {
      setSelectedTab("all");
    } else {
      setSelectedTab("sent");
    }
  }, [canLaunchCampaign]);

  const toggleSelection = (id) => {
    setSelectedMessages((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  // ======================================================
  // FILTERS
  // ======================================================

  const filteredOpportunities = useMemo(() => {
    // PRE-LAUNCH MODE
    if (canLaunchCampaign) {
      if (selectedTab === "all") {
        return opportunities;
      }

      return opportunities.filter(
        (o) => o.message_type === selectedTab
      );
    }

    // EXECUTED MODE
    if (selectedTab === "excluded") {
      return opportunities.filter(
        (o) => o.status !== "sent"
      );
    }

    // default = sent
    return opportunities.filter(
      (o) => o.status === "sent"
    );
  }, [selectedTab, opportunities, canLaunchCampaign]);
  const selectedOpportunities = useMemo(() => {
  return filteredOpportunities.filter((o) =>
    selectedMessages.includes(o.id)
  );
}, [filteredOpportunities, selectedMessages]);

const selectedCount = selectedOpportunities.length;

const selectedRevenue = selectedOpportunities.reduce(
  (acc, item) => acc + Number(item.expected_value || 0),
  0
);

const selectedRefillRevenue = selectedOpportunities
  .filter((o) => o.message_type === "refill")
  .reduce(
    (acc, item) => acc + Number(item.expected_value || 0),
    0
  );

const selectedReorderRevenue = selectedOpportunities
  .filter((o) => o.message_type === "reorder")
  .reduce(
    (acc, item) => acc + Number(item.expected_value || 0),
    0
  );

const selectedAtRiskRevenue = selectedOpportunities
  .filter((o) => o.message_type === "reward")
  .reduce(
    (acc, item) => acc + Number(item.expected_value || 0),
    0
  );

const selectedTotalRevenue =
  selectedRefillRevenue +
  selectedReorderRevenue +
  selectedAtRiskRevenue;

const percent = (val) =>
  selectedTotalRevenue > 0
    ? ((val / selectedTotalRevenue) * 100).toFixed(1)
    : 0;

const smsCostPerMessage = 2;

const selectedSMSCost = selectedCount * smsCostPerMessage;

const selectedROI =
  selectedSMSCost > 0
    ? Math.round(selectedRevenue / selectedSMSCost)
    : 0;


  // ======================================================
  // SEND FLOW
  // ======================================================

  const launchSMSPopup = () => {
    const selected = filteredOpportunities.filter((o) =>
      selectedMessages.includes(o.id)
    );

    const mapped = selected.map((o) => ({
      id: o.id,
      customer_name: o.customer_name,
      cust_phone: o.cust_phone,
      suggestion: o.suggested_message,
      total_amount: o.expected_value,
      action: {
        message: o.suggested_message,
      },
      type: o.message_type,
    }));

    setPopupTasks(mapped);
    setShowPopup(true);
  };

  // ======================================================
  // LOADING
  // ======================================================

  if (loading) {
    return (
      <>
        <SkeletonUILoader/>
      </>
    );
  }

 
  
  const sentOpportunities = opportunities.filter(
    (o) => o.status === "sent"
  );

  const pendingOpportunities = opportunities.filter(
    (o) => o.status !== "sent"
  );

  const sentCount = sentOpportunities.length;

  const sentRevenue = sentOpportunities.reduce(
    (acc, item) => acc + Number(item.expected_value || 0),
    0
  );

  const sentSMSCost = sentCount * smsCostPerMessage;

  const sentROI =
    sentSMSCost > 0
      ? Math.round(sentRevenue / sentSMSCost)
      : 0;

  const displayRevenue = canLaunchCampaign
    ? selectedRevenue
    : sentRevenue;

  const selectedLaunchCount =
    filteredOpportunities.filter((o) =>
      selectedMessages.includes(o.id)
    ).length;

  // ======================================================
  // UI
  // ======================================================
  
  return (
    <>
      <Helmet>
        <title>Recovery Campaign</title>
        <meta
          name="description"
          content={`Kadhr turns your sales data into quick, ready-to-send actions that keep customers coming back.`}
        />
      </Helmet>
      <section className="container mtopcon dashb">
        <div className="row">
          <div className="col-12 col-lg-2 dash-min mb-3">
            <div className="nav-left-sidebar sidebar-dark pc">
              <div className="slimScrollDiv">
                <div className="menu-list">
                  <nav className="navbar navbar-expand-lg navbar-light nav-l-con">
                    <div className="collapse navbar-collapse" id="navbarNav">
                      <Sidebar />
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-10 dash-max">
            <div className="row">
                <div className="container-fluid p-0 m-0">

                    {/* ========================================= */}
                    {/* HEADER */}
                    {/* ========================================= */}

                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">

                      <div>

                        <Link
                          to="/seller/retarget/runs"
                          className="text-decoration-none text-muted d-inline-flex align-items-center gap-2 mb-2"
                        >
                          <ArrowLeft size={16} />
                          Back to History
                        </Link>

                        <h3 className="fw-bold mb-1">
                          Recovery Campaign #{run?.id}
                        </h3>

                        <div className="d-flex align-items-center gap-3 flex-wrap text-muted font8">

                          <div className="d-flex align-items-center gap-1">
                            <FaCalendarAlt size={12} />
                            {new Date(
                              run?.created_at
                            ).toLocaleString()}
                          </div>

                          <div className="d-flex align-items-center gap-1">
                            <Clock size={12} />
                            Status:
                            <span className="text-dark fw-semibold tcone">
                              {run?.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      
                    </div>

                      <div className="card border-0 shadow-sm rounded-4 p-3 mb-4">

                          <div className="d-flex justify-content-between align-items-center mb-3">

                              <h5 className="fw-bold mb-0">
                              Revenue Forecast
                              </h5>

                              <Badge className="bg-primary-subtle text-primary font5">
                              by Customer Behavior
                              </Badge>

                          </div>

                          <h2 className="fw-bold mb-3">
                              KES{" "}
                              {Math.round(
                              displayRevenue
                              ).toLocaleString()}
                          </h2>

                          {(() => {

                              const sourceData = canLaunchCampaign
                                ? selectedOpportunities
                                : sentOpportunities;

                              const reorderRevenue = sourceData
                                .filter((o) => o.message_type === "reorder")
                                .reduce((acc, item) => acc + Number(item.expected_value || 0), 0);

                              const refillRevenue = sourceData
                                .filter((o) => o.message_type === "refill")
                                .reduce((acc, item) => acc + Number(item.expected_value || 0), 0);

                              const atRiskRevenue = sourceData
                                .filter((o) => o.message_type === "reward")
                                .reduce((acc, item) => acc + Number(item.expected_value || 0), 0);

                              const total =
                              reorderRevenue +
                              refillRevenue +
                              atRiskRevenue;

                              const percent = (val) =>
                              total > 0
                                  ? ((val / total) * 100).toFixed(1)
                                  : 0;

                              return (
                              <>
                                  <div
                                  className="d-flex overflow-hidden rounded"
                                  style={{
                                      height: "10px",
                  
                                  }}
                                  >
                                  {refillRevenue > 0 && (
                                      <div
                                      style={{
                                          width: `${percent(refillRevenue)}%`,
                                          background: "#00cb93",
                                      }}
                                      />
                                  )}

                                  {reorderRevenue > 0 && (
                                      <div
                                      style={{
                                          width: `${percent(reorderRevenue)}%`,
                                          background: "#f5c542",
                                      }}
                                      />
                                  )}

                                  {atRiskRevenue > 0 && (
                                      <div
                                      style={{
                                          width: `${percent(atRiskRevenue)}%`,
                                          background: "#ff0e2e",
                                      }}
                                      />
                                  )}
                                  </div>

                                  <div className="mt-3 d-flex flex-wrap gap-3 font8">

                                    <small>
                                        <span className="dott-mini bglgreen me-1"></span>
                                        Ready to reorder
                                    </small>

                                    <small>
                                        <span className="dott-mini bglorange me-1"></span>
                                        Upcoming
                                    </small>

                                    <small>
                                        <span className="dott-mini bgreg me-1"></span>
                                        At risk
                                    </small>

                                  </div>
                              </>
                              );
                          })()}
                          {/* ========================================= */}
                          {/* METRIC BADGES */}
                          {/* ========================================= */}

                          {canLaunchCampaign && (
                            <div className="d-flex flex-wrap gap-2 mb-0 mt-3">

                              <div
                                className="px-3 py-2 rounded-3"
                                style={{
                                  background: "#eefbf3",
                                  border: "1px solid #d7f5df",
                                }}
                              >
                                <div
                                  className="fw-bold font8"
                                  style={{
                                    color: "#16a34a",
                                  }}
                                >
                                  ⚡ ({selectedCount}) Opportunities
                                </div>

                                <div className="text-muted font7">
                                  Likely to convert
                                </div>
                              </div>

                              <div
                                className="px-3 py-2 rounded-3"
                                style={{
                                  background: "#f5f3ff",
                                  border: "1px solid #e9d5ff",
                                }}
                              >
                                <div
                                  className="fw-bold font8"
                                  style={{
                                    color: "#7c3aed",
                                  }}
                                >
                                  💰 KES {Math.round(selectedRevenue).toLocaleString()}
                                </div>

                                <div className="text-muted font7">
                                  Predicted revenue
                                </div>
                              </div>

                              <div
                                className="px-3 py-2 rounded-3"
                                style={{
                                  background: "#fff7ed",
                                  border: "1px solid #fed7aa",
                                }}
                              >
                                <div
                                  className="fw-bold font8"
                                  style={{
                                    color: "#ea580c",
                                  }}
                                >
                                  ✉️ KES {selectedSMSCost.toLocaleString()}
                                </div>

                                <div className="text-muted font7">
                                  Estimated SMS cost
                                </div>
                              </div>

                              <div
                                className="px-3 py-2 rounded-3"
                                style={{
                                  background: "#eff6ff",
                                  border: "1px solid #bfdbfe",
                                }}
                              >
                                <div
                                  className="fw-bold font8"
                                  style={{
                                    color: "#2563eb",
                                  }}
                                >
                                  📈 {selectedROI}x
                                </div>

                                <div className="text-muted font7">
                                  Estimated ROI
                                </div>
                              </div>

                            </div>
                          )}

                          <div className="mt-3">

                            {canLaunchCampaign && (
                              <button
                                className="btn btn-dark rounded-3 px-4 w-100"
                                onClick={launchSMSPopup}
                                disabled={selectedLaunchCount === 0}
                              >
                                <Send
                                  className="me-2"
                                  width={"18px"}
                                />
                                Launch Campaign ({selectedLaunchCount})
                              </button>
                            )}

                            {!canLaunchCampaign && (
                            <div
                              className="mt-0 px-2 py-2 rounded-3 font9"
                              style={{
                                background: "#fffaf0",
                                border: "1px solid #fed7aa",
                                color: "#9a3412",
                              }}
                            >
                              <div className="fw-bold mb-1">
                                Campaign completed
                              </div>

                              <div className="mb-3 text-muted font8">
                                This recovery campaign has already been delivered. Here’s the breakdown from the messages processed in this run.
                              </div>

                              <div className="d-flex flex-wrap gap-2">

                                <div
                                  className="px-3 py-2 rounded-3"
                                  style={{
                                    background: "#eefbf3",
                                    border: "1px solid #d7f5df",
                                  }}
                                >
                                  <div
                                    className="fw-bold font8"
                                    style={{
                                      color: "#16a34a",
                                    }}
                                  >
                                    ✉️ {sentCount}
                                  </div>

                                  <div className="text-muted font7">
                                    Messages sent
                                  </div>
                                </div>

                                <div
                                  className="px-3 py-2 rounded-3"
                                  style={{
                                    background: "#f5f3ff",
                                    border: "1px solid #e9d5ff",
                                  }}
                                >
                                  <div
                                    className="fw-bold font8"
                                    style={{
                                      color: "#7c3aed",
                                    }}
                                  >
                                    💰 KES {Math.round(sentRevenue).toLocaleString()}
                                  </div>

                                  <div className="text-muted font7">
                                    Forecast from sent messages
                                  </div>
                                </div>

                                <div
                                  className="px-3 py-2 rounded-3"
                                  style={{
                                    background: "#fff7ed",
                                    border: "1px solid #fed7aa",
                                  }}
                                >
                                  <div
                                    className="fw-bold font8"
                                    style={{
                                      color: "#ea580c",
                                    }}
                                  >
                                    🧾 KES {sentSMSCost.toLocaleString()}
                                  </div>

                                  <div className="text-muted font7">
                                    SMS delivery cost
                                  </div>
                                </div>

                                <div
                                  className="px-3 py-2 rounded-3"
                                  style={{
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                  }}
                                >
                                  <div
                                    className="fw-bold font8"
                                    style={{
                                      color: "#2563eb",
                                    }}
                                  >
                                    📈 {sentROI}x
                                  </div>

                                  <div className="text-muted font7">
                                    Forecast ROI
                                  </div>
                                </div>

                              </div>
                            </div>
                          )}

                          </div>
                      </div>
                      
                      {/* ========================================= */}
                      {/* FILTER + SEARCH */}
                      {/* ========================================= */}

                      <div className="card border-0 shadow-none bg-inherit p-0 mb-4">
                        <div className="row g-3 align-items-center">

                          <div className="col-12 col-lg-6">
                            <div className="d-flex gap-2 flex-wrap">

                              {canLaunchCampaign ? (
                                <>
                                  <button
                                    onClick={() => setSelectedTab("all")}
                                    className={`btn rounded-pill font8 ${
                                      selectedTab === "all"
                                        ? "btn-dark"
                                        : "btn-light border"
                                    }`}
                                  >
                                    All ({opportunities.length})
                                  </button>

                                  <button
                                    onClick={() => setSelectedTab("refill")}
                                    className={`btn rounded-pill font8 ${
                                      selectedTab === "refill"
                                        ? "btn-dark"
                                        : "btn-light border"
                                    }`}
                                  >
                                    Active
                                  </button>

                                  <button
                                    onClick={() => setSelectedTab("reorder")}
                                    className={`btn rounded-pill font8 ${
                                      selectedTab === "reorder"
                                        ? "btn-dark"
                                        : "btn-light border"
                                    }`}
                                  >
                                    Upcoming
                                  </button>

                                  <button
                                    onClick={() => setSelectedTab("reward")}
                                    className={`btn rounded-pill font8 ${
                                      selectedTab === "reward"
                                        ? "btn-dark"
                                        : "btn-light border"
                                    }`}
                                  >
                                    At Risk
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setSelectedTab("sent")}
                                    className={`btn rounded-pill font8 ${
                                      selectedTab === "sent"
                                        ? "btn-dark"
                                        : "btn-light border"
                                    }`}
                                  >
                                    Sent ({sentCount})
                                  </button>

                                  <button
                                    onClick={() => setSelectedTab("excluded")}
                                    className={`btn rounded-pill font8 ${
                                      selectedTab === "excluded"
                                        ? "btn-dark"
                                        : "btn-light border"
                                    }`}
                                  >
                                    Excluded ({pendingOpportunities.length})
                                  </button>
                                </>
                              )}

                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ========================================= */}
                      {/* OPPORTUNITY GRID */}
                      {/* ========================================= */}

                      <div className="row g-4 mb-5">
                        {filteredOpportunities.length === 0 ? (
                          <div className="col-12">
                            <div className="card border-0 shadow-sm rounded-4 p-5 text-left">
                              <AlertTriangle size={32} className="mb-3 text-muted" />
                              <div className="fw-semibold mb-1">
                                No opportunities found
                              </div>
                            </div>
                          </div>
                        ) : (
                          filteredOpportunities.map((opp) => (
                            <RetentionOpportunityCard
                              key={opp.id}
                              data={opp}
                              selected={selectedMessages.includes(opp.id)}
                              onSelect={() => toggleSelection(opp.id)}
                              disabled={!canLaunchCampaign}
                            />
                          ))
                        )}
                      </div>

                      {/* ========================================= */}
                      {/* SMS POPUP */}
                      {/* ========================================= */}

                      <SMSBulkPopup
                        show={showPopup}
                        onClose={() => setShowPopup(false)}
                        tasks={popupTasks}
                        smslink={""}
                        runUuid={runUuid}
                        onSuccess={() => {
                          setShowPopup(false);
                          fetchRun();
                        }}
                      />
                </div>
            </div>
          </div>
        </div>
        
      </section>
    </>
  );
};

export default RetentionRunDetailPage;